import { Observable, of } from "rxjs";
import { Epic, ofType } from "redux-observable";
import { catchError, map, mergeMap } from "rxjs/operators";
import buildQuery from "odata-query";
import * as _ from "lodash";

import { Action, Epoch, TS, Debug } from "@ctra/utils";

import types from "./types";
import actions from "./actions";
import { Change, CreateTrackerServerValues, Subject, TrackerSource, TrackerSourceList } from "./typings";
import { makeApiURL } from "../../utils/ajax";
import { EventScope } from "../events";

/**
 * Fetch question and answers from the QnA endpoint
 * @param action$
 * @param state$
 * @param Request
 */
const fetchSingleTracker: Epic = (action$, state$, { Request }) =>
  action$.pipe(
    ofType(types.FETCH_ASK_IDA_TRACKER.pending),
    mergeMap<ReturnType<typeof actions.fetchAskIdaTracker.start>, Observable<Promise<unknown>>>(
      ({ payload: { id } }) => {
        Debug.askIdaApi.info(`Fetching Ask Ida tracker ${id}`);

        const url = makeApiURL(`https://api<%= env %>.connecterra.ai/odata/IdaQnAs(${id})`)({});

        return Request.GET(url).pipe(
          map<{ response: TrackerSource }, Action>(({ response }) => {
            Debug.askIdaApi.info("Ask Ida tracker successfully fetched");

            return actions.fetchAskIdaTracker.fulfill(id, response);
          }),
          catchError<unknown, Observable<Action>>((error) => {
            Debug.askIdaApi.error("An error occurred while fetching Ask Ida tracker", { id, error });
            return of(actions.fetchAskIdaTracker.reject(id, error));
          })
        );
      }
    )
  );

/**
 * Fetch question and answers from the QnA endpoint
 * @param action$
 * @param state$
 * @param Request
 */
const fetchTrackersByQuery: Epic = (action$, state$, { Request }) =>
  action$.pipe(
    ofType(types.FETCH_ASK_IDA_TRACKER_LIST.pending),
    mergeMap<ReturnType<typeof actions.fetchTrackerList.start>, Observable<Promise<unknown>>>(
      ({ payload: { hash, query } }) => {
        Debug.askIdaApi.info("Fetching active Ask Ida trackers by query", { query });

        const url = makeApiURL(`https://api<%= env %>.connecterra.ai/odata/IdaQnAs${buildQuery(query)}`, {
          dev: "staging"
        })({});

        return Request.GET(url).pipe(
          map<{ response: { value: TrackerSourceList } }, Action>(({ response: { value } }) => {
            Debug.askIdaApi.info("Ask Ida tracker list successfully fetched");

            return actions.fetchTrackerList.fulfill(hash, value);
          }),
          catchError<unknown, Observable<Action>>((error) => {
            Debug.askIdaApi.error("An error occurred while fetching an Ask Ida tracker list by query", {
              query,
              error
            });

            return of(actions.fetchTrackerList.reject(hash, error));
          })
        );
      }
    )
  );

/**
 * Fetch question and answers from the QnA endpoint
 * @param action$
 * @param state$
 * @param Request
 */
const createAskIdaTracker: Epic = (action$, state$, { Request }) =>
  action$.pipe(
    ofType(types.CREATE_ASK_IDA_TRACKER.pending),
    mergeMap<ReturnType<typeof actions.createAskIdaTracker.start>, Observable<Promise<unknown>>>(
      ({
        payload: {
          event: {
            trackerLabel,
            id: eventID,
            farmId,
            category,
            subCategory,
            source,
            scope: {
              type: eventScopeType,
              ids: [eventScopeID]
            },
            startAt,
            endAt
          },
          formValues: { effects }
        }
      }) => {
        const url = makeApiURL(`https://api<%= env %>.connecterra.ai/odata/IdaQnAs`)({});

        const [finalDate] = _.map(effects, "date");

        /**
         * Make server values from the event and the form values
         */
        const serverValues: CreateTrackerServerValues = {
          period: false,
          subjectType:
            /* istanbul ignore next */ eventScopeType === EventScope.farm ? Subject.farm : Subject.herdGroup,
          herdGroup:
            /* istanbul ignore next */ eventScopeType === EventScope.herdGroup
              ? { HerdGroupID: +eventScopeID }
              : void 0,
          effects: _.map(effects, (effectDefinition) => _.omit(effectDefinition, "date")),
          changes: [
            {
              /**
               * Legacy values (to not break anything important)
               */
              change: Change.other,
              otherChange: trackerLabel,
              /**
               * New values, resolved from the related event
               */
              annotationId: eventID,
              source: _.defaultTo(source, void 0),
              category,
              subCategory
            }
          ],
          startDate: TS.asMoment(startAt).toISOString(),
          endDate: TS.asMoment(endAt).toISOString(),
          finalDate: finalDate as string,
          createdEpoch: Epoch.now(),
          startEpoch: Epoch.load(+TS.asMoment(startAt)),
          endEpoch: Epoch.load(+TS.asMoment(endAt)),
          finalEpoch: Epoch.load(+TS.asMoment(finalDate as string))
        };

        /**
         * Make a server payload from the above values
         * @type {{question: string, active: boolean, farmID: any}}
         */
        const serverPayload = {
          active: true,
          question: JSON.stringify(serverValues),
          farmID: farmId
        };

        Debug.askIdaApi.info("Creating new Ask Ida tracker", { serverValues, serverPayload });

        return Request.POST(url, {
          body: serverPayload
        }).pipe(
          map<{ response: TrackerSource }, Action>(({ response }) => {
            Debug.askIdaApi.info("Ask Ida tracker successfully created", { response });
            return actions.createAskIdaTracker.fulfill(response);
          }),
          catchError<unknown, Observable<Action>>((error) => {
            Debug.askIdaApi.error("An error occurred while creating the Ask Ida tracker", { error });
            return of(actions.createAskIdaTracker.reject(error));
          })
        );
      }
    )
  );

/**
 * Fetch question and answers from the QnA endpoint
 * @param action$
 * @param state$
 * @param Request
 */
const deleteAskIdaTracker: Epic = (action$, state$, { Request }) =>
  action$.pipe(
    ofType(types.DELETE_ASK_IDA_TRACKER.pending),
    mergeMap<ReturnType<typeof actions.deleteAskIdaTracker.start>, Observable<Promise<unknown>>>(
      ({ payload: { id } }) => {
        Debug.askIdaApi.info(`Deleting Ask Ida tracker ${id}`);

        const url = makeApiURL(`https://api<%= env %>.connecterra.ai/odata/IdaQnAs(${id})`)({});

        return Request.DELETE(url).pipe(
          map<unknown, Action>(() => {
            Debug.askIdaApi.info(`Ask Ida tracker ${id} successfully deleted`);

            return actions.deleteAskIdaTracker.fulfill(id);
          }),
          catchError<unknown, Observable<Action>>((error) => {
            if (_.get(error, "status")) {
              const { status } = error;

              // istanbul ignore next - we don't cover for non-404 statuses as it is done by the
              if (status === 404) {
                Debug.askIdaApi.info(`The IdaQnA object ${id} does not seem to exist anymore`);
                return of(actions.deleteAskIdaTracker.fulfill(id));
              }
            }

            Debug.askIdaApi.error("An error occurred while deleting the Ask Ida tracker", { id, error });
            return of(actions.deleteAskIdaTracker.reject(id, error));
          })
        );
      }
    )
  );

export default {
  fetchSingleTracker,
  createAskIdaTracker,
  deleteAskIdaTracker,
  fetchTrackersByQuery
};
